import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import { QrCodePreviewModalComponent } from '../../shared/modals/qr-code-preview-modal/qr-code-preview-modal.component';
import { UserModalComponent } from '../../shared/modals/user-modal/user-modal.component';
import { UserModalData } from '../../shared/models/modal-data.model';
import { IUser, UserRole, UserState } from '../../shared/models/user.model';
import { ROLE_LABELS } from '../../shared/constants/role-access';
import { ModalService } from '../../shared/services/modal.service';
import { ToastService } from '../../shared/services/toast.service';
import { UserService } from '../../shared/services/user.service';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent, QrCodePreviewModalComponent],
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
})
export class UsersComponent implements OnInit, OnDestroy {
  public users: IUser[] = [];
  public isLoading = false;

  public searchTerm = '';
  public searchTerms = new Subject<string>();

  public currentPage = 1;
  public pageSize = 10;
  public totalItems = 0;
  public totalPages = 1;

  public sortField = 'employeeId';
  public sortDirection = 'asc';

  public UserRole = UserRole;
  public UserState = UserState;

  public qrPreviewToken: string | null = null;
  public qrPreviewLabel = '';

  private subscriptions = new Subscription();

  constructor(
    private userService: UserService,
    private modalService: ModalService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.loadUsers();

    this.subscriptions.add(
      this.searchTerms.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
        this.currentPage = 1;
        this.onSearch();
      })
    );
  }

  public loadUsers(): void {
    this.isLoading = true;
    this.subscriptions.add(
      this.userService.getUsers(this.currentPage, this.pageSize, this.searchTerm, this.sortField, this.sortDirection).subscribe({
        next: response => {
          if (response.DATA) {
            this.users = response.DATA.data;
            this.totalItems = response.DATA.total;
            this.totalPages = response.DATA.totalPages;
          }
          this.isLoading = false;
        },
        error: () => {
          console.error('Error loading users');
          this.isLoading = false;
        },
      })
    );
  }

  public onSearch(): void {
    this.currentPage = 1;
    this.loadUsers();
  }

  public onPageChange(page: number): void {
    this.currentPage = page;
    this.loadUsers();
  }

  public onSort(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.loadUsers();
  }

  public getSortIcon(field: string): string {
    if (this.sortField !== field) {
      return 'unfold_more';
    }
    return this.sortDirection === 'asc' ? 'expand_less' : 'expand_more';
  }

  public getRoleName(role: UserRole): string {
    return ROLE_LABELS[role] ?? role;
  }

  public getStateName(state: UserState): string {
    return state === UserState.ACTIVE ? 'Active' : 'Inactive';
  }

  public copyQrToken(qrToken: string): void {
    if (!qrToken) return;

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(qrToken).then();
      return;
    }

    this.fallbackCopyToClipboard(qrToken);
  }

  public openQrCodePreview(user: IUser): void {
    if (!user.qrToken) return;

    this.qrPreviewToken = user.qrToken;
    this.qrPreviewLabel = `${user.employeeId}`;
  }

  public closeQrCodePreview(): void {
    this.qrPreviewToken = null;
    this.qrPreviewLabel = '';
  }

  private fallbackCopyToClipboard(text: string): boolean {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
      return document.execCommand('copy');
    } catch {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }

  public clearSearch(): void {
    this.searchTerm = '';
    this.searchTerms.next('');
  }

  public openCreateModal(): void {
    const data: UserModalData = { mode: 'create' };
    this.modalService
      .open(UserModalComponent, data)
      .then((user: IUser) => {
        if (user) {
          this.userService.createUser(user).subscribe({
            next: () => {
              this.toastService.showSuccess('Benutzer erfolgreich erstellt');
              this.loadUsers();
            },
            error: () => {
              console.error('Error creating user');
              this.toastService.showError('Fehler beim Erstellen des Benutzers');
            },
          });
        }
      })
      .finally(() => {
        this.modalService.close();
      });
  }

  public openEditModal(user: IUser): void {
    const data: UserModalData = { mode: 'edit', user };
    this.modalService
      .open(UserModalComponent, data)
      .then((updatedUser: IUser) => {
        if (updatedUser && updatedUser._id) {
          this.userService.updateUser(updatedUser._id, updatedUser).subscribe({
            next: () => {
              this.toastService.showSuccess('Benutzer erfolgreich aktualisiert');
              this.loadUsers();
            },
            error: () => {
              console.error('Error updating user');
            },
          });
        }
      })
      .catch(() => { })
      .finally(() => {
        this.modalService.close();
      });
  }

  public onSearchChange(): void {
    this.searchTerms.next(this.searchTerm);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
